import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface OperationLog {
  id: string;
  restaurant_id: string;
  user_id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  details: Record<string, any> | null;
  created_at: string;
  profile?: {
    full_name: string | null;
  };
}

export type LogAction =
  | "create"
  | "update"
  | "delete"
  | "open"
  | "close"
  | "auto_close"
  | "transfer"
  | "cancel"
  | "complete"
  | "send";

export type EntityType =
  | "order"
  | "product"
  | "category"
  | "table"
  | "command"
  | "command_entry"
  | "cash_register"
  | "cash_movement"
  | "payment_method"
  | "ingredient"
  | "stock_movement"
  | "user"
  | "notification"
  | "operational_period";

const actionLabels: Record<LogAction, string> = {
  create: "Criou",
  update: "Atualizou",
  delete: "Excluiu",
  open: "Abriu",
  close: "Fechou",
  auto_close: "Fechou automaticamente",
  transfer: "Transferiu",
  cancel: "Cancelou",
  complete: "Concluiu",
  send: "Enviou",
};

const entityLabels: Record<EntityType, string> = {
  order: "Pedido",
  product: "Produto",
  category: "Categoria",
  table: "Mesa",
  command: "Comanda",
  command_entry: "Entrada",
  cash_register: "Caixa",
  cash_movement: "Movimentação",
  payment_method: "Forma de Pagamento",
  ingredient: "Ingrediente",
  stock_movement: "Mov. Estoque",
  user: "Usuário",
  notification: "Notificação",
  operational_period: "Período",
};

export function useOperationLogs() {
  const { activeRestaurant } = useRestaurantContext();
  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalLogs, setTotalLogs] = useState(0);

  const fetchLogs = useCallback(
    async (options?: {
      page?: number;
      pageSize?: number;
      action?: string;
      entityType?: string;
      dateFrom?: Date;
      dateTo?: Date;
    }) => {
      if (!activeRestaurant) {
        setIsLoading(false);
        return;
      }

      const page = options?.page || 1;
      const pageSize = options?.pageSize || 50;
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      setIsLoading(true);
      try {
        let query = supabase
          .from("operation_logs")
          .select("*", { count: "exact" })
          .eq("restaurant_id", activeRestaurant.id)
          .order("created_at", { ascending: false })
          .range(from, to);

        if (options?.action) {
          query = query.eq("action", options.action);
        }
        if (options?.entityType) {
          query = query.eq("entity_type", options.entityType);
        }
        if (options?.dateFrom) {
          query = query.gte("created_at", options.dateFrom.toISOString());
        }
        if (options?.dateTo) {
          query = query.lte("created_at", options.dateTo.toISOString());
        }

        const { data, error, count } = await query;

        if (error) throw error;

        // Fetch user profiles separately
        const userIds = [...new Set((data || []).map((log: any) => log.user_id))];
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", userIds);

        const profileMap = new Map(
          (profiles || []).map((p: any) => [p.id, p])
        );

        const logsWithProfiles = (data || []).map((log: any) => ({
          ...log,
          profile: profileMap.get(log.user_id) || { full_name: null },
        }));

        setLogs(logsWithProfiles as OperationLog[]);
        setTotalLogs(count || 0);
      } catch (error) {
        console.error("Error fetching logs:", error);
        toast.error("Erro ao carregar logs");
      } finally {
        setIsLoading(false);
      }
    },
    [activeRestaurant]
  );

  const logOperation = async (
    action: LogAction,
    entityType: EntityType,
    entityId?: string,
    details?: Record<string, any>
  ) => {
    if (!activeRestaurant) return;

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) return;

      await supabase.from("operation_logs").insert({
        restaurant_id: activeRestaurant.id,
        user_id: user.id,
        action,
        entity_type: entityType,
        entity_id: entityId || null,
        details: details || null,
      });
    } catch (error) {
      console.error("Error logging operation:", error);
    }
  };

  const getActionLabel = (action: string) =>
    actionLabels[action as LogAction] || action;

  const getEntityLabel = (entity: string) =>
    entityLabels[entity as EntityType] || entity;

  return {
    logs,
    totalLogs,
    isLoading,
    fetchLogs,
    logOperation,
    getActionLabel,
    getEntityLabel,
  };
}
